import { useTranslation } from 'react-i18next'
import {
    STATUS_SHIPMENT_TO_BE_SELF_COLLECTED,
    STATUS_SHIPMENT_DELIVERED,
} from '../constants/orderStatuses'
import { getPickupDisplayName } from '../utils/pickupName'
import { buildDirectionsUrl } from '../utils/maps'
import { Button } from './Button'
import { ArrowRightIcon } from './Icons/ArrowRightIcon'
import { PICKUP_CHIP_ANCHOR_ID } from '../constants/pickup'

const STATE_PRE_ARRIVAL = 'preArrival'
const STATE_READY = 'ready'
const STATE_COLLECTED = 'collected'

function deriveState(shippingStatus) {
    if (shippingStatus === STATUS_SHIPMENT_TO_BE_SELF_COLLECTED) return STATE_READY
    if (shippingStatus === STATUS_SHIPMENT_DELIVERED) return STATE_COLLECTED
    return STATE_PRE_ARRIVAL
}

export function PickupActionChip({ order, orderId }) {
    const { t } = useTranslation()
    const metadata = order?.click_and_collect_metadata
    const state = deriveState(order?.shipping_status)
    const name = getPickupDisplayName(metadata, t)
    const directionsUrl = buildDirectionsUrl(metadata)

    const containerClasses =
        state === STATE_READY
            ? 'rounded-md border border-blue-200 bg-blue-50 p-4'
            : 'rounded-md bg-gray-50 p-4'

    const titleKey = `pickupActionChip.${state}.title`
    const descriptionKey = `pickupActionChip.${state}.description`

    return (
        <div id={PICKUP_CHIP_ANCHOR_ID} className={`${containerClasses} overflow-hidden`} data-testid="pickup-action-chip">
            <div className="flex flex-col gap-1 min-w-0">
                <div className="font-semibold text-gray-800 break-words">{name}</div>
                {typeof metadata?.address === 'string' && metadata.address && (
                    <div className="text-sm text-gray-600 break-words">{metadata.address}</div>
                )}
                {typeof metadata?.contactDetails === 'string' && metadata.contactDetails && (
                    <div className="text-sm text-gray-600 break-words">
                        {t('pickupActionChip.hoursLabel')} {metadata.contactDetails}
                    </div>
                )}
                {state === STATE_PRE_ARRIVAL && directionsUrl && (
                    <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-700 self-start inline-flex items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                    >
                        {t('pickupActionChip.getDirections')} <ArrowRightIcon className="ml-1 h-3 w-3" />
                    </a>
                )}
            </div>
            <div className="my-3 h-px bg-gray-200" />
            <div className="flex flex-col gap-1 min-w-0">
                <div className="font-semibold text-gray-800 break-words">{t(titleKey)}</div>
                <div className="text-sm text-gray-600 break-words">
                    {t(descriptionKey, { pickup_name: name })}
                </div>
                {state === STATE_READY && directionsUrl && (
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                        <Button
                            variant="outline"
                            size="sm"
                            label={t('pickupActionChip.getDirections')}
                            onClick={() => window.open(directionsUrl, '_blank', 'noopener,noreferrer')}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
